import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  mutation createKit($data: createKitInput!) {
    createKit(data: $data) {
      id
      status
      error {
        message
        statusCode
      }
    }
  }
`;

const initialState: TYPES.MutationResponseState = {
  result: null,
  error: null,
  loading: false
};

export const createKitRequest = createAsyncThunk<
  TYPES.MutationResponse,
  {data: TYPES.CreateKitInput;},
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'kit/create_kit',
  async (params: {data: TYPES.CreateKitInput;}, { extra: client }) => {
    try {
      const response = await client.mutate({
        mutation: GRAPHQL,
        variables: params
      });
      return response.data.createKit;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const createKitSlice = createSlice({
  name: 'create_kit',
  initialState: initialState,
  reducers: {
    createKitClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(createKitRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(createKitRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(createKitRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { createKitClear } = createKitSlice.actions;

export const createKit = createSelector(
  (state: RootState) => state.kit.createKit.result,
  (state: RootState) => state.kit.createKit.error,
  (state: RootState) => state.kit.createKit.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default createKitSlice.reducer;